import {
    app,
    Component,
    ojs,
    each,
    state,
    v,
    component,
} from "modular-openscriptjs";
import {
    pageInfo,
    scrollToTop,
    getStore,
    store,
    deepEqual,
    empty,
} from "../../helpers";
import { api, dc } from "../../declarations";
import SearchResultProductView from "../Views/SearchResultProductView";
import SolidIcon from "../Blocks/SolidIcon";
import Spinner from "../Blocks/Spinner";
import TopCategoryView from "./Search/TopCategoryView";
import InfiniteScroll from "../Blocks/InfiniteScroll";

const h = app("h");

ojs(
    SearchResultProductView,
    SolidIcon,
    Spinner,
    TopCategoryView,
    InfiniteScroll,
);

export default class SearchPage extends Component {
    categories = state([]);
    loading = state(true);
    businessId = null;
    categoriesFetched = state(false);
    searchQuery = state("");

    page = 1;
    lastPage = false;

    $$system = {
        needs: {
            search: (ed, event) => this.debounceSearch(),
        },
    };

    constructor() {
        super();
        this.searchQuery.listener(this);
    }

    initPage() {
        this.page = 1;
        this.lastPage = false;
    }

    render(...args) {
        this.businessId = window.currentCompany?.id;
        const businessName = window.currentCompany?.name || "Inventory";
        pageInfo(`Business Inventory - ${businessName}`);
        scrollToTop();

        if (!this.businessId) {
            return h.div(
                { class: "text-center py-5 py-md-5" },
                "Please select a business first.",
            );
        }

        return h.div(
            { class: "py-4 mx-auto w-100 container" },

            h.div(
                { class: "mb-4 d-flex align-items-center gap-3" },
                h.button(
                    {
                        class: "btn btn-white border shadow-sm rounded-circle p-2",
                        onclick: "history.back()",
                    },
                    h.SolidIcon("arrow-left"),
                ),
                h.h2({ class: "fw-bold mb-0" }, "Business Inventory"),
            ),

            v(this.searchQuery, (q) => {
                if (q.value?.trim().length > 0) {
                    return this.renderSearchResults();
                }
                return this.renderCategoriesGrid();
            }),
            ...args,
        );
    }

    renderCategoriesGrid() {
        return h.div(
            {},
            h.h4({ class: "fw-bold mb-4" }, "Main Categories"),
            v(this.categories, (cats) => {
                if (this.loading.value && cats.value.length === 0) {
                    return h.div(
                        { class: "row g-3" },
                        each([1, 2, 3, 4, 5, 6], () =>
                            h.div(
                                { class: "col-6 col-md-4 col-lg-3" },
                                h.div(
                                    {
                                        class: "placeholder-glow rounded-4 bg-light",
                                        style: "aspect-ratio: 1/1;",
                                    },
                                    h.div({
                                        class: "placeholder w-100 h-100 rounded-4",
                                    }),
                                ),
                            ),
                        ),
                    );
                }

                if (cats.value.length === 0) {
                    return h.div(
                        { class: "text-center py-5 text-muted" },
                        "No categories found with products.",
                    );
                }

                return h.div(
                    { class: "row g-3" },
                    ...cats.value.map((cat) =>
                        h.div(
                            { class: "col-6 col-md-4 col-lg-3" },
                            h.TopCategoryView(cat),
                        ),
                    ),
                );
            }),
        );
    }

    renderSearchResults() {
        return h.InfiniteScroll({
            child: h.div({
                class: "row gap-3",
                hook: "sp-search-results-list",
            }),
            callback: (entries, observer, marker) => {
                if (entries[0]?.isIntersecting && !this.lastPage) {
                    this.getResults(marker);
                }
            },
        });
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (!c) return;
        c.fetchCategories();
    }

    debounceTimer = null;
    debounceSearch() {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.search(), 500);
    }

    async getResults(marker) {
        if (this.lastPage) {
            return;
        }

        const holder = dom.get("[hook='sp-search-results-list']");
        let spinner = null;

        if (marker) {
            spinner = h.Spinner({
                class: "p-3",
                parent: marker,
                resetParent: true,
            });
        }

        let res = await api.get(
            `companies/${this.businessId}/products/search`,
            {
                page: this.page,
                interval: 10,
                q: this.searchQuery.value,
            },
            { silent: true },
        );

        if (!res && marker) {
            spinner?.remove();
            return;
        }

        const products = res.data.result.data;

        if (empty(products) && res.data.result.current_page == 1) {
            h.div(
                {
                    class: "d-flex flex-column align-items-center justify-content-center py-5 mt-4 bg-light rounded-4 border",
                    style: "min-height: 350px; border-style: dashed !important; border-width: 2px !important;",
                    parent: holder,
                    resetParent: true,
                },
                h.div(
                    { class: "bg-white p-4 rounded-circle shadow-sm mb-4 d-flex align-items-center justify-content-center", style: "width: 80px; height: 80px;" },
                    h.i({ class: "bi bi-box-seam fs-1 text-primary" })
                ),
                h.h4({ class: "fw-bold text-dark mb-2" }, "No Products Found"),
                h.p(
                    { class: "text-secondary mb-4 text-center px-4", style: "max-width: 400px;" },
                    "You might have not added that product to your inventory yet. Would you like to add it now?",
                ),
                h.button(
                    h.i({ class: "bi bi-plus-lg fs-5" }),
                    h.span({ class: "fw-semibold" }, "Add Product"),
                    {
                        class: "btn btn-primary btn-lg rounded-pill px-5 shadow-sm d-flex align-items-center gap-2 transition-all",
                        listeners: {
                            click: () =>
                                app("router").to("business.products.add", {
                                    companyId: window.currentCompany?.id,
                                }),
                        },
                    }
                ),
            );
        } else {
            for (let product of products) {
                let id = `p-${product?.id}`;
                let rendered = false;

                if (dom.get(`[search-result-product-id="${id}"]`, holder)) {
                    rendered = true;
                }

                if (!rendered) {
                    h.SearchResultProductView(product, {
                        $class: "col-12 col-md-6 col-lg-4",
                        parent: holder,
                        $search_result_product_id: id,
                    });
                }
            }

            if (this.page >= res.data.result.last_page) {
                this.lastPage = true;
            } else {
                h.button(
                    h.i({
                        class: "bi bi-arrow-down-circle me-2 fs-5",
                        style: "line-height: 0;",
                    }),
                    "Load More",
                    {
                        parent: marker,
                        resetParent: true,
                        class: "btn btn-outline-primary d-flex align-items-center justify-content-center mx-auto my-4 rounded-pill px-4 fw-semibold",
                        listeners: {
                            click: () => this.getResults(marker),
                        },
                    },
                );
            }
        }

        this.page = res.data.result.current_page + 1;

        spinner?.remove();
    }

    async search() {
        this.searchQuery.value = dc.searchQuery?.trim();

        this.page = 1;
        this.lastPage = false;

        this.getResults();
    }

    async fetchCategories() {
        if (this.categoriesFetched.value) return;

        this.loading.value = true;
        try {
            // Fetch categories that have products in this business inventory
            const res = await api.get(
                `companies/${this.businessId}/categories/top`,
            );
            if (res) {
                this.categories.value = res.data.categories;
                this.categoriesFetched.value = true;
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.loading.value = false;
        }
    }
}

ojs(SearchPage);
